import clsx from 'clsx';
import { render, unmountComponentAtNode } from 'react-dom';
import * as React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { CloseButton } from '../CloseButton';
// @ts-ignore
import toastErrorIcon from '../../assets/icons/toast-error.svg';
import { getRandomKey } from '../../util/randoms';
import {
  IListPageToast,
  IListPageToastProps,
  IToastProps,
  IBaseToast,
  ToastType
} from './Toast.types';
import lightTheme from '../../theme/light';

const ToastContainer = styled.div<{ toastType: ToastType }>`
  display: inline-flex;
  justify-content: space-between;
  width: 310px;
  align-items: center;
  border-radius: 4px;
  padding: 0 16px;
  position: relative;
  box-shadow: 1px 5px 5px -5px #727272;
  color: ${({ toastType, theme }) =>
    toastType === ToastType.DANGER
      ? theme.colors.toast.dangerFont
      : theme.colors.toast.successFont};
  background: ${({ toastType, theme }) =>
    toastType === ToastType.DANGER
      ? theme.colors.toast.dangerBg
      : theme.colors.toast.successBg};
  &:after {
    content: '';
    position: absolute;
    left: 0;
    height: 100%;
    width: 10px;
    background-color: ${({ toastType, theme }) =>
      toastType === ToastType.DANGER
        ? theme.colors.toast.dangerFont
        : theme.colors.toast.successFont};
    border-radius: 5px 0 0 5px;
  }
`;

const ToastIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ToastWrapper = styled.div<{ staticMode: boolean }>`
  z-index: 1000;
  position: ${({ staticMode }) => (staticMode ? 'static' : 'absolute')};
  margin: ${({ staticMode }) => (staticMode ? '0 15px' : '')};
`;

const ToastContent = styled.div`
  display: flex;
  img {
    margin-right: 10px;
  }
`;

export const destroyToast = (id: string) => {
  const toastEl = document.getElementById(id);
  if (toastEl) {
    unmountComponentAtNode(toastEl);
    // Also remove container
    toastEl.remove();
  }
};

const getToastPosition = (toast: IBaseToast) => {
  let left = toast.left ? `${toast.left.toString()}px` : 'unset';
  let top = toast.top ? `${toast.top.toString()}px` : 'unset';
  const right = toast.right ? `${toast.right.toString()}px` : 'unset';
  const bottom = toast.bottom ? `${toast.bottom.toString()}px` : 'unset';
  if (!toast.top && !toast.bottom) {
    top = '0px';
  }
  if (!toast.left && !toast.right) {
    left = '0px';
  }
  return {
    left,
    top,
    right,
    bottom,
    zIndex: 9999999
  };
};

export const Toast: React.FC<IToastProps> = (props: IToastProps) => {
  const { toast, children, id } = props;
  if (toast.duration !== undefined && toast.duration !== 0) {
    setTimeout(() => destroyToast(id), toast.duration);
  } else if (toast.duration !== 0) {
    setTimeout(() => destroyToast(id), 4000);
  }
  const position = getToastPosition(toast);
  return (
    <ToastWrapper staticMode={toast.static} style={{ ...position }}>
      <div className={toast.className ?? ''}>{children}</div>
    </ToastWrapper>
  );
};

export const GlobalToast: React.FC<IListPageToastProps> = (
  props: IListPageToastProps
) => {
  const { toast, id } = props;

  return (
    <ThemeProvider theme={lightTheme}>
      <Toast
        id={id}
        toast={{
          ...toast,
          top: toast.top ?? 178,
          right: toast.right ?? 70
        }}
      >
        <ToastContainer
          toastType={toast.toastClassName as ToastType}
          className={clsx(['text-button'])}
        >
          <ToastContent>
            <ToastIconContainer>
              {toast.icon && (
                <img
                  className={toast.iconClassName ?? ''}
                  src={toast.icon}
                  alt="toast-icon"
                />
              )}
            </ToastIconContainer>
            <div className={`text-button ${toast.textClassName ?? ''}`}>
              {toast.message}
            </div>
          </ToastContent>
          <CloseButton
            className={toast.closeButtonClassName ?? ''}
            onClick={() => destroyToast(id)}
            type="button"
          />
        </ToastContainer>
      </Toast>
    </ThemeProvider>
  );
};

export const renderToast = (
  toast: (id: string) => JSX.Element,
  containerId?: string
): string => {
  const toastId = getRandomKey();
  const containerNode = document.createElement('div');

  containerNode.setAttribute('id', toastId.toString());

  if (containerId) {
    document.querySelector(`#${containerId}`).appendChild(containerNode);
  } else {
    document.body.appendChild(containerNode);
  }

  render(toast(toastId.toString()), containerNode);
  return toastId.toString();
};

export const showToast = (
  toast: IListPageToast,
  containerId?: string
): string => {
  const {
    colorType,
    message,
    className,
    textClassName,
    closeButtonClassName
  } = toast;
  const icon = colorType === 'Danger' ? toastErrorIcon : null;
  const toastClassName =
    colorType === 'Danger' ? ToastType.DANGER : ToastType.SUCCESS;

  return renderToast(
    id => (
      <GlobalToast
        id={id}
        toast={{
          icon,
          colorType,
          message,
          className,
          toastClassName,
          textClassName,
          closeButtonClassName
        }}
      />
    ),
    containerId
  );
};

export default {
  showToast
};
